<template>
    <v-app>
        <v-main>
            <!-- Install Banner for installation as a WEB APP -->
            <transition name="fade-slide">
                <div
                    v-if="$route.path === '/' && installBtn && $vuetify.breakpoint.smAndDown"
                    class="app-install-banner"
                >
                    <v-btn
                        icon
                        small
                        class="app-install-banner__close"
                        @click="hideBanner"
                    >
                        <v-icon small>
                            {{ mdiClose }}
                        </v-icon>
                    </v-btn>
                    <span class="app-install-banner__text"> Install app for faster access </span>
                    <v-btn
                        small
                        rounded
                        class="app-install-banner__btn"
                        @click="installer"
                    >
                        Install
                    </v-btn>
                </div>
            </transition>
            <router-view />
            <v-dialog
                v-model="showLoginExpired"
                width="500"
                persistent
            >
                <v-card>
                    <v-card-title class="text-h5">
                        Login Expired!
                    </v-card-title>
                    <v-card-text>Your login has expired.</v-card-text>
                    <v-card-actions>
                        <v-spacer />
                        <v-btn
                            color="green darken-1"
                            text
                            @click="logout()"
                        >
                            Logout
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
            <v-dialog
                v-if="$route.name !== 'forgotPass'"
                v-model="isAccountLocked"
                width="500"
                persistent
            >
                <v-card>
                    <v-card-title class="text-h5">
                        Account Locked!
                    </v-card-title>
                    <v-card-text>It's been more than {{ exp_days }} days since your last password change. Please <b style="color: darkred;">update your password</b> to regain access.</v-card-text>
                    <v-card-actions>
                        <v-spacer />
                        <v-btn
                            text
                            color="black darken-1"
                            @click="logout()"
                        >
                            Logout
                        </v-btn>
                        <v-btn
                            style="font-weight: 500;"
                            color="green darken-1 white--text"
                            @click="$router.push({name: 'forgotPass', query: {provider: null, expired: null}})"
                        >
                            update password
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
            <base-loading />
        </v-main>
    </v-app>
</template>

<script>
import BaseLoading from './components/BaseLoading.vue'
import { mapGetters, mapActions } from 'vuex'
import { mdiClose } from '@mdi/js'

export default {
    components: { BaseLoading  },
    data: () => ({
        mdiClose,
        installBtn: false,
        installer: undefined,
        deferredPrompt: null, // Store event reference safely
        showLoginExpired: false,
        exp_days: process.env.VUE_APP_PWORD_EXPIRY
    }),
    computed: {
        ...mapGetters(['isSessionExpired', 'isAccountLocked', 'isAuthorizedData']),
        isAdminRoute() {
            return window.location.pathname.includes('admin')
        }
    },
    watch: {
        isSessionExpired(newVal) {
            if (newVal) {
                this.showLoginExpired = newVal
            }
        }
    },
    created() {
        // Intercept and prevent default browser install prompt
        window.addEventListener('beforeinstallprompt', (e) => {
            e.preventDefault()
            this.deferredPrompt = e

            // Delay showing the UI button by 2 seconds after event fires
            setTimeout(() => {
                if (this.deferredPrompt) {
                    this.installBtn = true
                }
            }, 1000)
        })

        // Define installer action
        this.installer = () => {
            if (!this.deferredPrompt) return

            this.installBtn = false
            this.deferredPrompt.prompt()
            this.deferredPrompt.userChoice.then((result) => {
                if (result.outcome === 'accepted') {
                    window.location.reload()
                }
                this.deferredPrompt = null
            })
        }
    },
    methods: {
        ...mapActions(['getCsrfToken', 'userLogout', 'isAuthorized']),
        hideBanner() {
            this.installBtn = false
        },
        async logout() {
            await this.userLogout()
        }
    }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  font-family: "Montserrat", sans-serif;
}
:root {
  color-scheme: only light; /* Prevents Chrome Auto-Dark from applying */
}
::v-deep(.session-expired-overlay) {
  z-index: 9999 !important;
}

/* Scroll bar */
/* width */
::-webkit-scrollbar {
  width: 9px;
  height: 8px;
}

/* Track */
::-webkit-scrollbar-track {
  background: #f1f1f1;
}

/* Handle */
::-webkit-scrollbar-thumb {
  background: rgb(194, 194, 194);
  border-radius: 20px;
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
  background: #555;
}
/* Root container */
.app-install-banner {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);

  display: flex;
  align-items: center;
  gap: 14px;

  padding: 10px 18px;
  border-radius: 999px;

  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: #fff;

  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(6px);

  z-index: 1000;
}

/* Text */
.app-install-banner__text {
  font-size: 14px;
  opacity: 0.95;
  white-space: nowrap;
}

/* Install button */
.app-install-banner__btn {
  border: 1px solid rgba(255, 255, 255, 0.7);
  background: transparent !important;
  color: #fff !important;
  font-weight: 600;
  text-transform: none;
}

.app-install-banner__btn:hover {
  background: rgba(255, 255, 255, 0.15) !important;
}

/* Close button */
.app-install-banner__close {
  background: rgba(0, 0, 0, 0.15) !important;
  color: #fff !important;
}

.app-install-banner__close:hover {
  background: rgba(0, 0, 0, 0.3) !important;
}

/* Animation (namespaced too) */
.app-install-banner-enter-active,
.app-install-banner-leave-active {
  transition: all 0.3s ease;
}

.app-install-banner-enter,
.app-install-banner-leave-to {
  opacity: 0;
  transform: translate(-50%, 20px);
}
@media (max-width: 600px) {
  .app-install-banner {
    width: calc(100% - 24px);
    border-radius: 12px;
    justify-content: space-between;
  }

  .app-install-banner__text {
    font-size: 13px;
  }
}   
</style>
